import * as yup from "yup";
import { AppDispatch } from "../../../../framework/redux/store";
import i18n from "i18next";
import { TransportOrderProductDetails } from "../../../../models/transport/transportOrderProductDetails";
import { ProductForm } from "./productTable";
import { saveTransportOrderWithNotification } from "../../../../framework/redux/slices/transportOrderDetailsSlice";
import { TransportOrderDetailsDto } from "../../../../models/transport/transportOrder";
import { TransportOrderProductItem } from "../../../../models/product/product";

export const productTableSx = {
    sx: {
        marginBottom: 2,
        ".MuiTableCell-root": {
            minWidth: "57px",
            p: 0.1,
            fontSize: "0.8rem",
            border: "none"
        },
        ".MuiTableCell-root:nth-of-type(1)": {
            minWidth: "10px"
        },
        ".MuiTableCell-root:nth-of-type(2)": {
            minWidth: "10px"
        }
    }
};

interface ValidateProps {
    context: yup.TestContext<yup.AnyObject>;
    value?: number;
    text?: string;
    field: string;
    validateSet?: boolean;
    validateLimitsMin?: boolean;
    validateLimitsMax?: boolean;
    validateInteger?: boolean;
    validateDecimal?: boolean;
}

const validateNumber = ({ value, field, context, validateSet, validateLimitsMin, validateLimitsMax, validateInteger, validateDecimal }: ValidateProps) => {
    const DECIMAL_SCALE = 2;
    const MIN = 0;
    const MAX = 999999;
    if (validateSet && value === undefined || value === null) {
        return context.createError({
            message: i18n.t("validation.fieldIsRequired", { field })
        });
    }
    if (validateLimitsMin && value && value < MIN) {
        return context.createError({
            message: i18n.t("validation.fieldMinValue", { field, value: MIN })
        });
    }
    if (validateLimitsMax && value && value > MAX) {
        return context.createError({
            message: i18n.t("validation.fieldMaxValue", { field, value: MAX })
        });
    }
    if (validateInteger && value && !Number.isInteger(value)) {
        return context.createError({
            message: i18n.t("validation.fieldIntValue", { field })
        });
    }
    const tooManyDecimals = String(value).split(".")[1]?.length > DECIMAL_SCALE;
    if (validateDecimal && value && !isNaN(value) && tooManyDecimals) {
        return context.createError({
            message: i18n.t("validation.fieldDecimalValue", { field })
        });
    }
    return true;
};

const validateText = ({ text, field, context, validateSet }: ValidateProps) => {
    if (validateSet) {
        if (text === undefined || text === null || text === "") {
            return context.createError({
                message: i18n.t("validation.fieldIsRequired", { field })
            });
        }
    }
    return true;
};

export const validationSchema = yup.object({
    rows: yup.array().of(yup.object().shape({
        productId: yup.string().test((text, context) =>
            validateText({ context, text, field: i18n.t("transport.parcel.name"), validateSet: true })
        ),
        amount: yup.number().test((value, context) =>
            validateNumber({ context, value, field: i18n.t("transport.parcel.amount"), validateSet: true, validateInteger: false, validateDecimal: true, validateLimitsMin: true, validateLimitsMax: true })
        )
    }))
});

export const emptyStrsToNulls = (obj: TransportOrderProductDetails | undefined) => JSON.parse(
    JSON.stringify(obj, (_, v: TransportOrderProductDetails) => (v === "" ? null : v))
) as TransportOrderProductDetails;

const getChangedRows = <T extends Record<string, any>>(values: T, initialValues: T) => {
    return Object.entries(values).reduce((acc: Partial<T>, [key, value]) => {
        const hasChanged = initialValues[key as keyof T] !== value;
        if (hasChanged) acc[key as keyof T] = value;
        return acc;
    }, {});
};

export const getChangedProductData = (formData: ProductForm, originalRows: TransportOrderProductDetails[]) => {
    const originalRowsDeletedExcluded = originalRows.filter(r => formData?.rows?.some(p => p.id === r.id));
    const changedRows = Object.values(getChangedRows(formData?.rows, originalRowsDeletedExcluded));
    const normalized = changedRows.map((r => emptyStrsToNulls(r)));
    return {
        created: normalized.filter(p => !p.transportOrderId),
        changed: normalized.filter(p => p.transportOrderId),
        deleted: originalRows.filter(r => !formData?.rows.some(p => p.productId === r.productId))
    };
};

export const handleProductRequest = async(
    order: TransportOrderDetailsDto,
    productRows: TransportOrderProductItem[],
    dispatch: AppDispatch
) => {
    const updatedOrder = updateProducts(productRows, order);
    await dispatch(
        saveTransportOrderWithNotification({ updateDto: updatedOrder, orderId: updatedOrder.id })
    ).unwrap();
};

function updateProducts(productRows: TransportOrderProductItem[], order: TransportOrderDetailsDto) {
    return {
        ...order,
        products: [...productRows]
    };
}
